/**
 * Slot classification policy — the ONE decider for "is this element a
 * plain-text slot", shared verbatim by the Worker (MCP patch/read tools, the
 * client-edit op engine) and the browser (the client editor's affordance
 * gating). Moved from `ai/mcp/mcp-project-read-tools.js` at CE-0 so the two
 * sides can never drift; that module re-exports for its existing importers.
 *
 * Pure data + one pure function. NO imports — this file must load in the
 * Worker, in Node tests, and in the browser unchanged.
 */

/**
 * Tags whose contents are not editable prose: void elements (no text to hold),
 * media and embedded content, form controls, and the two raw-text elements —
 * writing "text" into a `<script>` writes code.
 */
export const NOT_TEXT_TAGS = new Set([
  'area',
  'base',
  'br',
  'col',
  'embed',
  'hr',
  'img',
  'input',
  'link',
  'meta',
  'param',
  'source',
  'track',
  'wbr',
  'audio',
  'canvas',
  'iframe',
  'object',
  'picture',
  'select',
  'svg',
  'textarea',
  'video',
  'script',
  'style',
  'template'
]);

/** `data-pb-editable` values that are NOT prose, when the attribute survives at all. */
export const NOT_TEXT_ROLES = new Set(['image', 'svg', 'logo', 'container']);

/**
 * Is this element a plain-text slot?
 *
 * The persisted marker is `data-pb-eid` ALONE. `data-pb-editable` is injected
 * at render time by `editing/editable-utils.js` and STRIPPED again by
 * `stampEditableIds` (`stripTempAttrs`, :542) before the html is saved — so
 * requiring it here would match nothing in a real project. The classification
 * is therefore structural, and the attribute is honoured only when it happens
 * to be present.
 *
 * @returns {null|string} null when it is a text slot, else the refusal reason
 */
export function textSlotProblem(el) {
  const tag = String(el.tagName || '').toLowerCase();
  if (NOT_TEXT_TAGS.has(tag)) return 'not_text';
  const role = el.getAttribute('data-pb-editable');
  if (role && NOT_TEXT_ROLES.has(role)) return 'not_text';
  for (const child of el.childNodes || []) {
    if (child.nodeType !== 3) return 'has_markup';
  }
  return null;
}
